import { QueryType } from 'components/commons/IssueCrashFilter/IssueCrashFilterExUtil';
import { Input, Select, TreeSelect } from 'antd';
import { CsSelect } from 'components/antd-extension/CsSelect';
import { CsTreeSelect } from 'components/antd-extension/CsTreeSelect';
import { UserIdDescriptionConfigUtil } from 'utils/user-id-description-config-util';
import { ze } from 'utils/zhEn';
import React, { useMemo } from 'react';

/**
 * 用户ID备注别名搜索组件
 * 
 * 功能背景：
 * 每个用户ID（userId）会对应一个descKeyValueList，表示用户id对应的备注说明。
 * 可能有多条备注，比如某个用户id对应的descKeyValueList数据结构是：
 * [{key: '用户名', value: '张三'}, {key: '测试账号', value: '12345'}]
 * 
 * 组件逻辑：
 * 1. 先选择一个key（如"用户名"或"测试账号"），作为查询的备注字段
 * 2. 再从树形选择器中选择对应的用户（根据选择的key显示对应的value）
 * 
 * 复杂性说明：
 * 1. 重复值处理：多个用户ID可能对应相同的备注值（如多个用户都叫"张三"）
 *    - 解决方案：使用树形结构，重复值作为父节点，具体用户作为子节点
 * 2. 父子节点选择：需要支持选择父节点来快速选择所有同名用户
 *    - 解决方案：使用TreeSelect的treeCheckable和SHOW_PARENT策略
 * 3. 数据转换：TreeSelect显示的值与实际存储的userId数组需要双向转换
 *    - 显示时：如果某标签下所有用户都选中，显示父节点；否则显示具体子节点
 *    - 存储时：将父节点值展开为对应的所有子节点userId
 */
const UserIdByAliasSearch = ({
  fieldValue,
  onChangeFieldValue = () => {},
  isReadOnly,
}) => {
  const {
    queryType: nullableQueryType,
    subField, // 用subField来存放ID备注别名的字段，后台搜索用不到这个信息
    terms,
  } = fieldValue || {};

  const userIdDescriptionInfo = UserIdDescriptionConfigUtil.useCurrentAppParseCsvContentResult();
  const {
    descriptionFieldList,
    userIdToDescKeyValueList,
  } = userIdDescriptionInfo;
  const descriptionFieldOptions = useMemo(() => {
    return descriptionFieldList.map(x => ({ label: x, value: x }));
  }, [descriptionFieldList]);

  // 生成树形数据结构
  // 核心逻辑：处理重复label的情况，将相同备注值的用户分组到父子节点结构中
  const userIdByAliasTreeData = useMemo(() => {
    if (!subField) {
      return [];
    }

    const userIdList = Object.keys(userIdToDescKeyValueList);

    // 第一步：创建映射 - 相同备注值的用户ID归为一组
    // 格式：{ "张三": [userData1, userData2], "李四": [userData3] }
    const labelToUserIdsMap = {};

    userIdList.forEach((userId) => {
      const descKeyValueList = userIdToDescKeyValueList[userId];
      const nullableMatchedKeyValue = descKeyValueList?.find(x => x.key === subField);
      const label = nullableMatchedKeyValue?.value || userId;

      if (!labelToUserIdsMap[label]) {
        labelToUserIdsMap[label] = [];
      }
      labelToUserIdsMap[label].push({
        userId,
        descKeyValueList,
      });
    });

    // 第二步：生成树形数据结构
    const treeData = [];

    Object.entries(labelToUserIdsMap).forEach(([label, userDataList]) => {
      if (userDataList.length === 1) {
        // 情况1：该备注值只对应一个用户，直接作为扁平节点
        const userData = userDataList[0];
        treeData.push({
          title: label,
          value: userData.userId,
          key: userData.userId,
          // 为搜索添加纯文本字段：只使用label
          searchText: label,
        });
      } else {
        // 情况2：该备注值对应多个用户（重复值），创建父子节点结构
        const children = userDataList.map((userData) => {
          // 获取除了当前选择字段外的其他备注字段，用于区分同名用户
          const otherFields = userData.descKeyValueList
            ?.filter(x => x.key !== subField)
            .map(x => `${x.key}: ${x.value}`)
            .join('\n') || '';

          // 子节点显示：用户ID + 其他备注信息（用于区分重复值）
          const childTitle = (
            <div>
              <div style={{ fontSize: '14px' }}>
                {ze('用户ID：', 'User ID: ')}{userData.userId}
              </div>
              {otherFields && (
                <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'pre-line' }}>
                  {otherFields}
                </div>
              )}
            </div>
          );

          // 为子节点搜索准备纯文本字段：用户ID + 所有备注信息的值
          const allDescValues = userData.descKeyValueList
            ?.map(x => x.value)
            .join(' ') || '';
          const childSearchText = `${userData.userId} ${allDescValues}`;

          return {
            title: childTitle,
            value: userData.userId,
            key: userData.userId,
            // 子节点搜索字段：包含用户ID和所有备注信息的值
            searchText: childSearchText,
          };
        });

        treeData.push({
          title: label, // 父节点显示重复的备注值
          value: `${label}__PARENT_NODE__`, // 父节点用特殊后缀标识，避免与实际userId冲突
          key: `${label}__PARENT_NODE__`,
          children,
          // 父节点搜索字段：只搜索label（备注值）
          searchText: label,
        });
      }
    });

    return treeData;
  }, [subField, userIdToDescKeyValueList]);

  // 数据转换：将实际存储的userId数组转换为TreeSelect应该显示的值
  // 转换逻辑：如果某个父节点下的所有子节点都被选中，则显示父节点；否则显示具体的子节点
  // 这样配合SHOW_PARENT策略，可以正确显示选中状态
  const treeSelectValue = useMemo(() => {
    if (!terms || !Array.isArray(terms) || terms.length === 0) {
      return [];
    }

    const selectedValues = new Set();
    const termSet = new Set(terms);

    // 遍历树形数据，决定显示父节点还是子节点
    userIdByAliasTreeData.forEach(node => {
      if (node.children) {
        // 有子节点：检查是否所有子节点都被选中
        const allChildrenSelected = node.children.every(child => termSet.has(child.value));
        if (allChildrenSelected) {
          // 所有子节点都选中：显示父节点（TreeSelect会自动显示为父节点选中状态）
          selectedValues.add(node.value);
        } else {
          // 部分子节点选中：显示具体的子节点
          node.children.forEach(child => {
            if (termSet.has(child.value)) {
              selectedValues.add(child.value);
            }
          });
        }
      } else {
        // 无子节点：直接判断是否选中
        if (termSet.has(node.value)) {
          selectedValues.add(node.value);
        }
      }
    });

    return Array.from(selectedValues);
  }, [terms, userIdByAliasTreeData]);

  if (nullableQueryType && nullableQueryType !== QueryType.TERMS) {
    return <Input
      disabled={true}
      style={{ width: '100%' }}
      value={JSON.stringify(fieldValue)}
    />;
  }

  return <div style={{ display: 'flex' }}>
    <Select
      style={{ width: '120px', flex: '0 0 auto' }}
      options={descriptionFieldOptions}
      optionFilterProp='name'
      optionLabelProp='name'
      value={ subField }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        onChangeFieldValue({
          ...fieldValue,
          queryType: QueryType.TERMS,
          not: false,
          subField: v,
        });
      }}
    />
    { <CsTreeSelect
      style={{ width: '100%', fontSize: '14px' }}
      treeCheckable={true}
      multiple={true}
      allowClear
      showSearch={true}
      treeData={userIdByAliasTreeData}
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      value={ treeSelectValue }
      listHeight={640}
      // 自定义搜索逻辑：使用节点的searchText字段进行搜索
      // 父节点：搜索label（备注值）
      // 子节点：搜索用户ID和所有备注信息的值
      filterTreeNode={(inputValue, treeNode) => {
        if (!inputValue) return true;
        const searchText = treeNode.searchText || '';
        return searchText.toLowerCase().includes(inputValue.toLowerCase());
      }}
      onChange={(v, label, extra) => {
        if (isReadOnly) {
          return;
        }

        let finalValues = v || [];

        // onChange数据转换：将TreeSelect的选择值转换为实际要存储的userId数组
        // 由于使用了SHOW_PARENT策略，TreeSelect会自动处理父子节点的选择关系
        // 但我们需要将父节点值展开为对应的所有子节点userId
        const expandParentNodes = (values) => {
          const expandedValues = new Set();

          values.forEach(value => {
            if (value.endsWith('__PARENT_NODE__')) {
              // 选中了父节点：将其展开为所有子节点的userId
              const parentNode = userIdByAliasTreeData.find(node => node.key === value);
              if (parentNode && parentNode.children) {
                parentNode.children.forEach(child => {
                  expandedValues.add(child.value);
                });
              }
            } else {
              // 选中了子节点：直接添加userId
              expandedValues.add(value);
            }
          });

          return Array.from(expandedValues);
        };

        // 转换为最终存储的userId数组
        finalValues = expandParentNodes(finalValues);

        onChangeFieldValue({
          ...fieldValue,
          queryType: QueryType.TERMS,
          not: false,
          terms: finalValues,
        });
      }}
    /> }
  </div>;
};

export default UserIdByAliasSearch;
